import { normalizePromptPayId } from '@pos/promptpay';

import type {
  Branch,
  BranchManualPaymentChannel,
  BranchPaymentMethodConfig,
  BranchPaymentSettings,
  PromptPayPersonalConfig,
} from '../../types/domain';

export interface PromptPayAccountInput {
  promptpayId: string;
  promptpayIdType: 'mobile' | 'national_id';
  accountName: string;
}

/** `all` means the store-wide account, which new branches also pick up. */
export interface BranchSelection {
  all: boolean;
  branchIds: string[];
}

export interface PromptPayAccountRow {
  key: string;
  source: 'store' | 'branch';
  account: PromptPayAccountInput;
  branchIds: string[];
}

const PROMPTPAY_LABEL = 'พร้อมเพย์ส่วนบุคคล';
const PROMPTPAY_SORT_ORDER = 3;

const promptPayMethod = (
  settings: BranchPaymentSettings | undefined,
): BranchPaymentMethodConfig | undefined =>
  settings?.methods.find((method) => method.method === 'qr_personal');

export function isValidPromptPayAccount(
  account: Pick<PromptPayAccountInput, 'promptpayId' | 'promptpayIdType'>,
): boolean {
  try {
    return Boolean(
      normalizePromptPayId(account.promptpayId, account.promptpayIdType),
    );
  } catch {
    return false;
  }
}

function usableAccount(
  method: BranchPaymentMethodConfig | undefined,
): PromptPayAccountInput | null {
  const config = method?.config?.qrPersonal;
  if (!method?.active || !config?.promptpayId || !config.promptpayIdType) {
    return null;
  }
  const accountName = config.accountName?.trim();
  if (!accountName) return null;
  const account = {
    promptpayId: config.promptpayId,
    promptpayIdType: config.promptpayIdType,
    accountName,
  };
  return isValidPromptPayAccount(account) ? account : null;
}

function storeAccount(
  settings: readonly BranchPaymentSettings[],
): PromptPayAccountInput | null {
  const method = promptPayMethod(settings.find((row) => row.branchId === null));
  const mode = method?.config?.qrPersonal?.mode;
  return mode === 'disabled' || mode === 'inherit'
    ? null
    : usableAccount(method);
}

/** Mirrors the POS resolver: the account a cashier at this branch would see. */
function effectiveAccount(
  branchId: string,
  settings: readonly BranchPaymentSettings[],
): { source: 'store' | 'branch'; account: PromptPayAccountInput } | null {
  const store = storeAccount(settings);
  const inherited = store ? { source: 'store' as const, account: store } : null;
  const method = promptPayMethod(
    settings.find((row) => row.branchId === branchId),
  );
  if (!method) return inherited;
  const mode = method.config?.qrPersonal?.mode;
  if (mode === 'disabled') return null;
  if (mode === 'inherit') return inherited;
  const own = usableAccount(method);
  if (own) return { source: 'branch', account: own };
  return mode === 'override' ? null : inherited;
}

export function promptPayAccountRows(
  branches: readonly Branch[],
  settings: readonly BranchPaymentSettings[],
): PromptPayAccountRow[] {
  const rows = new Map<string, PromptPayAccountRow>();
  const store = storeAccount(settings);
  if (store) {
    rows.set('store', {
      key: 'store',
      source: 'store',
      account: store,
      branchIds: [],
    });
  }
  for (const branch of branches) {
    const effective = effectiveAccount(branch.id, settings);
    if (!effective) continue;
    const { account, source } = effective;
    const key =
      source === 'store'
        ? 'store'
        : `branch:${account.promptpayId}:${account.accountName}`;
    const row = rows.get(key) ?? { key, source, account, branchIds: [] };
    row.branchIds.push(branch.id);
    rows.set(key, row);
  }
  return [...rows.values()];
}

function withPromptPayConfig(
  branchId: string | null,
  config: PromptPayPersonalConfig,
  settings: readonly BranchPaymentSettings[],
): BranchPaymentSettings {
  const current = settings.find((row) => row.branchId === branchId);
  const existing = promptPayMethod(current);
  return {
    branchId,
    methods: [
      // Legacy reference_manual rows flag every manual sale as missing evidence.
      ...(current?.methods ?? []).filter(
        (method) =>
          method.method !== 'qr_personal' &&
          method.method !== 'reference_manual',
      ),
      {
        method: 'qr_personal',
        displayLabel: existing?.displayLabel || PROMPTPAY_LABEL,
        active: config.mode !== 'disabled',
        requiresReference: false,
        requiresEvidence: false,
        sortOrder: existing?.sortOrder ?? PROMPTPAY_SORT_ORDER,
        config: { qrPersonal: config },
      },
    ],
  };
}

function toChangedRecords(
  configs: ReadonlyMap<string | null, PromptPayPersonalConfig>,
  settings: readonly BranchPaymentSettings[],
): BranchPaymentSettings[] {
  return [...configs].flatMap(([branchId, config]) => {
    const current = promptPayMethod(
      settings.find((row) => row.branchId === branchId),
    )?.config?.qrPersonal;
    return JSON.stringify(current) === JSON.stringify(config)
      ? []
      : [withPromptPayConfig(branchId, config, settings)];
  });
}

/** The records to write so `selection` uses `account`, and nothing else moves. */
export function planPromptPaySave(input: {
  account: PromptPayAccountInput;
  selection: BranchSelection;
  previous: PromptPayAccountRow | null;
  branches: readonly Branch[];
  settings: readonly BranchPaymentSettings[];
}): BranchPaymentSettings[] {
  const { account, selection, previous, branches, settings } = input;
  const override: PromptPayPersonalConfig = { mode: 'override', ...account };
  const configs = new Map<string | null, PromptPayPersonalConfig>();

  if (selection.all) {
    configs.set(null, override);
    for (const branch of branches) {
      const method = promptPayMethod(
        settings.find((row) => row.branchId === branch.id),
      );
      if (method) configs.set(branch.id, { mode: 'inherit' });
    }
    return toChangedRecords(configs, settings);
  }

  for (const branchId of previous?.branchIds ?? []) {
    configs.set(branchId, { mode: 'disabled' });
  }
  if (previous?.source === 'store') configs.set(null, { mode: 'disabled' });
  for (const branchId of selection.branchIds) configs.set(branchId, override);
  return toChangedRecords(configs, settings);
}

export function planPromptPayRemoval(
  row: PromptPayAccountRow,
  settings: readonly BranchPaymentSettings[],
): BranchPaymentSettings[] {
  const configs = new Map<string | null, PromptPayPersonalConfig>();
  if (row.source === 'store') {
    configs.set(null, { mode: 'disabled' });
  } else {
    for (const branchId of row.branchIds) {
      configs.set(branchId, { mode: 'disabled' });
    }
  }
  return toChangedRecords(configs, settings);
}

export const channelBranchIds = (
  channelId: string,
  branches: readonly Branch[],
  assignments: readonly BranchManualPaymentChannel[],
): string[] =>
  branches
    .filter((branch) =>
      assignments.some(
        (row) =>
          row.channelId === channelId &&
          row.branchId === branch.id &&
          row.active,
      ),
    )
    .map((branch) => branch.id);

export function planChannelAssignments(input: {
  channelId: string;
  name: string;
  selectedBranchIds: readonly string[];
  branches: readonly Branch[];
  assignments: readonly BranchManualPaymentChannel[];
  nextSortOrder: number;
}): BranchManualPaymentChannel[] {
  const { channelId, name, selectedBranchIds, nextSortOrder } = input;
  return input.branches.flatMap((branch) => {
    const active = selectedBranchIds.includes(branch.id);
    const existing = input.assignments.find(
      (row) => row.channelId === channelId && row.branchId === branch.id,
    );
    if (!existing && !active) return [];
    if (
      existing &&
      existing.active === active &&
      (existing.displayLabel ?? name) === name
    ) {
      return [];
    }
    return [
      {
        branchId: branch.id,
        channelId,
        displayLabel: name,
        active,
        sortOrder: existing?.sortOrder ?? nextSortOrder,
      },
    ];
  });
}

export function branchScopeLabel(
  branchIds: readonly string[],
  branches: readonly Branch[],
): string {
  if (branchIds.length === 0) return 'ยังไม่ได้เปิดใช้สาขาไหน';
  if (branches.length > 1 && branchIds.length === branches.length) {
    return 'ทุกสาขา';
  }
  return branches
    .filter((branch) => branchIds.includes(branch.id))
    .map((branch) => branch.short)
    .join(', ');
}
